import React, { Fragment } from 'react';

import { Color, FontSize, FontWeight } from '../../../typings/design-tokens';
import { slugifyCharacters } from '../../../utils/slugify';
import { makeClassNames } from '../../../utils/styles';
import GoalBadge from '../../atoms/goal-badge/goal-badge';
import Text from '../../atoms/text/text';
import Team, { IconPosition, ITeam, TeamLogoSize } from '../team/team';

import './teams-tabs.scss';

export interface ITeamsTabs {
  idHome: string;
  idAway: string;
  home: ITeam;
  away: ITeam;
  hasInput?: boolean;
  roundLabel?: string;
  onChange?: (value: 'home' | 'away') => void;
  initialValue?: 'home' | 'away';
  hasDesktopInput?: boolean;
  hasCta?: boolean;
}

const ICON_POSITION: IconPosition = 'right';
const COLOR: Color = 'inherit';

const classNames = (hasRoundLabel: boolean, hasDesktopInput: boolean, hasInput: boolean) =>
  makeClassNames({
    input: 'teams-tabs__input',
    tabs: [
      'teams-tabs__tabs',
      hasDesktopInput && 'teams-tabs__tabs--desktop-input',
      hasDesktopInput && hasInput && 'teams-tabs__tab--clickable',
    ],
    tabLeft: ['teams-tabs__tab', 'teams-tabs__tab--left', !hasRoundLabel && 'teams-tabs__tab--goalbadge'],
    tabRight: ['teams-tabs__tab', 'teams-tabs__tab--right', !hasRoundLabel && 'teams-tabs__tab--goalbadge'],
  });

const customClassName = (classname: string) => makeClassNames({ custom: `${classname}` });

/**
 * Utility function to get team formation
 */
export const getTeamFormation = (
  formation: string,
  classname: string,
  fontsize: FontSize,
  fontweight: FontWeight,
  color: Color
) => {
  const cn = customClassName(classname);
  return (
    <Text className={cn.custom} tag="span" fontSize={fontsize} fontWeight={fontweight} color={color}>
      {slugifyCharacters(formation)}
    </Text>
  );
};

const TeamsTabs = ({
  idHome,
  idAway,
  home,
  away,
  /* istanbul ignore next: default value */ hasInput = true,
  /* istanbul ignore next: default value */ roundLabel = '',
  /* istanbul ignore next: default value */ initialValue = 'home',
  /* istanbul ignore next: default value */ hasDesktopInput = false,
  /* istanbul ignore next: default value */ hasCta = false,
  onChange,
}: ITeamsTabs) => {
  const hasRoundLabel: boolean = roundLabel.length > 0;
  const cn = classNames(hasRoundLabel, hasDesktopInput, hasInput);
  const inputsGroup = `tab-${idHome}-${idAway}`;

  const FONT_SIZE: FontSize = hasInput ? '14--22' : '18--22'; // 18--22 for non clickable TEAM TABS
  const LOGO_SIZE: TeamLogoSize = hasInput ? '23--28' : '30'; // 30 for non clickable TEAM TABS
  const FONT_FORMATION_SIZE: FontSize = hasInput ? '14--18' : '18';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (onChange && (value === 'home' || value === 'away')) {
      onChange(value);
    }
  };

  const inputs = hasInput ? (
    <Fragment>
      <input
        type="radio"
        value="home"
        className={cn.input}
        id={idHome}
        name={inputsGroup}
        data-input-left
        defaultChecked={initialValue === 'home'}
        onChange={handleChange}
      />
      <input
        type="radio"
        value="away"
        className={cn.input}
        id={idAway}
        name={inputsGroup}
        data-input-right
        onChange={handleChange}
        defaultChecked={initialValue === 'away'}
      />
    </Fragment>
  ) : null;

  return (
    <Fragment>
      {inputs}
      <div
        className={cn.tabs}
        data-tabs
        data-inputs={hasInput}
        data-type={home.hasOwnProperty('formation') && away.hasOwnProperty('formation') ? 'formations' : null}
      >
        <label className={cn.tabLeft} htmlFor={idHome} data-tab-left data-team-name={home.name}>
          <Team {...home} colorSchema="dark" color={COLOR} fontSize={FONT_SIZE} logoSize={LOGO_SIZE} ellipsis />
          {!hasRoundLabel && <GoalBadge goal={home.goal || 0} />}
          {home.formation &&
            !hasCta &&
            getTeamFormation(home.formation, 'formation-home', FONT_FORMATION_SIZE, '400', COLOR)}
        </label>
        {hasRoundLabel && <GoalBadge roundLabel={roundLabel} />}
        <label className={cn.tabRight} htmlFor={idAway} data-tab-right data-team-name={away.name}>
          <Team
            {...away}
            color={COLOR}
            fontSize={FONT_SIZE}
            logoSize={LOGO_SIZE}
            iconPosition={ICON_POSITION}
            ellipsis
          />
          {!hasRoundLabel && <GoalBadge goal={away.goal || 0} />}
          {away.formation &&
            !hasCta &&
            getTeamFormation(away.formation, 'formation-away', FONT_FORMATION_SIZE, '400', COLOR)}
        </label>
      </div>
    </Fragment>
  );
};

export default TeamsTabs;
